import axios from 'axios';
import { AuthService } from '../services/authService';
import { isForceRefreshActive } from '../services/cacheRefreshContext';
import { TokenStore } from '../services/TokenStore';
import { getCsrfToken } from '../utils/csrfCookie';

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // send the httpOnly refreshToken cookie automatically
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: any) => void;
  config: any;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.config._retry = true;
      prom.resolve(api(prom.config));
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use(
  (config) => {
    const token = TokenStore.getActiveAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Only the analytics-cached endpoints honour `refresh`. The campaign list
    // and detail reads cache their EMV + engagement rate too, so they are in;
    // the prefix covers /campaign/get-campaigns and /campaign/get-campaign/:id
    // and nothing else under /campaign.
    if (
      isForceRefreshActive() &&
      ['/analytics', '/dashboard-kpis', '/campaign/get-campaign'].some((p) =>
        config.url?.startsWith(p),
      )
    ) {
      config.params = { ...config.params, refresh: 'true' };
    }
    const csrfToken = getCsrfToken();
    if (csrfToken && config.method?.toLowerCase() !== 'get') {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // NOTE: the token-refresh call itself must NEVER go through this `api`
    // instance — a 401 from it re-entering this branch while isRefreshing=true
    // would be parked in failedQueue forever (deadlock → infinite loading).
    // AuthService.refreshToken() therefore uses a bare, interceptor-free
    // client (see refreshClient in authService.ts).
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await AuthService.refreshToken();
        isRefreshing = false;

        if (newToken) {
          processQueue(null, newToken);
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          // Session dead — refreshToken() already cleared the tokens, so
          // isAuthenticated() is false and this redirect lands on the signin
          // form instead of being bounced back by the route guards.
          processQueue(error, null);
          const currentPath = window.location.pathname + window.location.search;
          window.location.href = `/signin?redirect=${encodeURIComponent(currentPath)}`;
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Transient failure (network/timeout/5xx) — tokens were kept, the
        // session may still be valid. Do NOT navigate: redirecting to /signin
        // while isAuthenticated() is true makes PublicRoutes/Signin bounce
        // the user back, creating a reload loop that hammers the backend.
        // Reject and let the caller surface a retryable error.
        isRefreshing = false;
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
